import { STUDENT_SESSION_TTL } from '@/constant';
import { formatName } from '@/utils/name';
import { useApolloClient } from '@apollo/client';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNotifications } from '@toolpad/core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import { DEFAULT_CODE_SIZE, StudentBadge } from '../../components/StudentBadge';
import { CREATE_STUDENT_SESSION, CreateStudentSessionResponse, CreateStudentSessionVariables, GET_STUDENTS, GetStudentsResponse } from '../../gql';
import './StudentPrintBadgeDialog.module.css';

interface Badge {
    token: string;
    caption?: string[];
}

type Props = {
    open: boolean;
    onClose: () => void;
    userIds: readonly string[];
};

export const StudentPrintBadgeDialog = ({ open, onClose, userIds }: Props) => {
    // provider
    const { t } = useTranslation();

    // ui state
    const [badges, setBadges] = useState<Record<string, Badge | Error>>({});
    const [printing, setPrinting] = useState(false);
    const notifications = useNotifications();

    // printing ref
    const contentRef = useRef<HTMLDivElement>(null);
    const print = useReactToPrint({
        contentRef,
        onBeforePrint: async () => {
            setPrinting(true);
        },
        onAfterPrint: () => {
            setPrinting(false);
        },
    });

    // client
    const client = useApolloClient();

    // handle session creation
    useEffect(() => {
        if (open) {
            setBadges({});

            client
                .query<GetStudentsResponse>({
                    query: GET_STUDENTS,
                    variables: {
                        where: {
                            id: {
                                in: userIds,
                            },
                        },
                    },
                })
                .then(async (reponse) => {
                    const studentRecords = reponse.data.users.reduce(
                        (acc, user) => ({
                            ...acc,
                            [user.studentProfile.id]: {
                                id: user.id,
                                studentProfileId: user.studentProfile.id,
                                name: formatName(user.studentProfile.familyName, user.studentProfile.givenName),
                                studentNumber: user.studentProfile.studentNumber,
                                class: user.studentProfile.class.className,
                            },
                        }),
                        {} as Record<string, { id: string; studentProfileId: string; name: string; studentNumber: string; class: string }>,
                    );

                    await Promise.allSettled(
                        Object.entries(studentRecords).map(([studentProfileId, studentRecord]) => {
                            return client
                                .mutate<CreateStudentSessionResponse, CreateStudentSessionVariables>({
                                    mutation: CREATE_STUDENT_SESSION,
                                    variables: {
                                        studentProfileId: studentProfileId,
                                        ttl: STUDENT_SESSION_TTL,
                                    },
                                })
                                .then((result) => ({ ...result.data, userId: studentRecord.id }));
                        }),
                    ).then((results) => {
                        const fulfilled = results.filter((result) => result.status === 'fulfilled');

                        // handle fulfilled
                        const badges: Record<string, Badge | Error> = {};
                        for (const result of fulfilled) {
                            if (!result.value || !result.value.authenticateWithClassroom) {
                                badges[result.value.userId] = new Error(`No data for user ${result.value.userId}`);
                            } else if (result.value?.authenticateWithClassroom.__typename === 'AuthenticateWithClassroomFailure') {
                                badges[result.value.userId] = new Error(`Failed to create badge for ${result.value.userId}: ${result.value.authenticateWithClassroom.message}`);
                            } else {
                                const user = studentRecords[result.value.authenticateWithClassroom.item.id];
                                if (user) {
                                    badges[user.id] = { token: result.value.authenticateWithClassroom.sessionToken, caption: [`${user.name} (${user.studentNumber})`, `${user.class}`] };
                                }
                            }
                        }

                        // handle rejected
                        const rejected = reponse.data.users.filter((user) => !Object.keys(badges).includes(user.id));
                        for (const result of rejected) {
                            badges[result.id] = new Error(`Failed to create badge for ${result.name}`);
                        }

                        setBadges(badges);
                    });
                })
                .catch((error) => {
                    notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                });
        }
    }, [client, notifications, userIds, open]);

    return (
        <Dialog open={open} maxWidth={'xl'} onClose={onClose}>
            <DialogTitle>{t('student.actions.studentBadge')}</DialogTitle>
            <DialogContent>
                <div ref={contentRef}>
                    <Grid container spacing={2} sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
                        {userIds.map((id) => (
                            <Grid key={id}>
                                {badges[id] instanceof Error ? (
                                    <Box sx={{ minHeight: DEFAULT_CODE_SIZE, minWidth: DEFAULT_CODE_SIZE, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <Alert severity='error'>{badges[id].message}</Alert>
                                    </Box>
                                ) : (
                                    <StudentBadge token={badges[id]?.token} caption={badges[id]?.caption} />
                                )}
                            </Grid>
                        ))}
                    </Grid>
                </div>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={async () => {
                        onClose();
                    }}
                >
                    {t('common.cancel')}
                </Button>
                <Button loading={printing} variant='contained' onClick={() => print()} loadingIndicator={<CircularProgress size={16} />}>
                    {t('common.print')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
